import type { ICirrusReaderSignature, IHttpHeadResponse } from "../shared/CirrusReaderModel";
import { MediaTypeDetector } from "@colibrio/colibrio-reader-framework/colibrio-core-io-mediatypedetector";

const { environment } = useEnvironment();
const cdnUrl = (environment.value == 'production') ? 'cdn': `${environment.value}.cdn`;
let cirrusSig: ICirrusReaderSignature | null = null;
let isLegacy: boolean = false;

const getCdnUrl = (): string => {
    const cirrusConfigStore = useCirrusConfigStore();
    const readerIdStore = useReaderIdStore();
    const publicationKeyBase64 = btoa(cirrusConfigStore.config?.public.publication?.key ?? "");
    const url = new URL(`https://${cdnUrl}.colibriocirrus.com/${isLegacy ? 'legacy/': ''}c/${readerIdStore.readerId}/b/${publicationKeyBase64}`);
    if (cirrusSig){
        url.searchParams.delete('Policy');
        url.searchParams.delete('Key-Pair-Id');
        url.searchParams.delete('Signature');

        url.searchParams.append('Policy', cirrusSig.p);
        url.searchParams.append('Key-Pair-Id', cirrusSig.k);
        url.searchParams.append('Signature', cirrusSig.s);
    }
    return url.toString();
}
export const useCirrusReaderHttpRequest = (user?: string, sessionToken?: string) => {
    const { 
        $cryptography,
    } = useNuxtApp();
    const signedFetch = useSignedFetch();
    const cirrusRestApi = useCirrusRestApi();
    const cirrusConfigStore = useCirrusConfigStore();

    /**
     * authoriseSession()
     * Accepts a Cirrus Publication Session Token (PST) and exchanges it for a Reader Session Token (RST)
     * @param sessionToken Cirrus Publication Session Token (PST)
     * 
     * @returns ICirrusReaderSignature | null - Returns null when the PST Auth process is not required
     */
    const authoriseSession = async () => {
        const deviceId = await $cryptography.fingerprint.deviceId();
        const bodyObj: {
            tenantId: string | null;
            userId: string | null;
            publicationKey?: string;
            isLegacy?: boolean;
        } = {
            "tenantId": useReaderIdStore().readerId, 
            "userId": user ?? "", 
            "publicationKey": cirrusConfigStore.config.public.publication?.key,
        };

        if (cirrusConfigStore.config.private.authentication.authType == "AWS" && cirrusConfigStore.config.private.authentication.role){
            bodyObj.isLegacy = isLegacy = true;
        }

        const opts: SignedFetchOptions = {
            headers: {
                'x-cirrus-device-id': deviceId,
            },
            fetchInit: {
                method: 'POST',
                body: JSON.stringify(bodyObj)
            },
        }
        if (sessionToken){
            opts['authorization'] = `Bearer ${sessionToken}`;
        }
        cirrusSig = await signedFetch.signedFetchJson<ICirrusReaderSignature>(cirrusRestApi.buildUrl('/publication/auth'), opts).then((response) => { 
            return response.body;
        }).catch((err) => {
            console.error(err);
            throw showError({
                fatal: true,
                status: 403,
                statusText: "Unable to authorise this reading session",
            });
        });
    }

    const refreshSession = () =>{
        const bodyObj: {
            grant_type: string;
            refresh_token?: string;
            isLegacy?: boolean;
        } = {
            "grant_type": "refresh", 
            "refresh_token": cirrusSig?.tokens.refresh, 
        };

        if (cirrusConfigStore.config.private.authentication.authType == "AWS" && cirrusConfigStore.config.private.authentication.role){
            bodyObj.isLegacy = isLegacy = true;
        }

        return fetch(cirrusRestApi.buildUrl('/publication/refresh'), {
            method: 'POST',
            body: JSON.stringify(bodyObj),
            headers: {
                'Authorization': `Bearer ${cirrusSig?.tokens.rst}`,
            },
        }).then(async (response) => { 
            return await response.json();
        }).then(async (result) => {
            cirrusSig = result;
            return true;
        }).catch ((err) => {
            console.error(err);
            throw showError({
                fatal: true,
                status: 403,
                statusText: 'Unable to refresh your session. Please refresh your browser.'
            });
        });
    }

    const sendRangeRequest = async (start: number, end: number, size: number): Promise<ArrayBuffer> => {
        const deviceId = await $cryptography.fingerprint.deviceId();
        if (!cirrusSig) await authoriseSession();

        return await useRetryFunction(
            () => signedFetch.signedFetchWithResponseBody(getCdnUrl(), {
                range: `bytes=${start}-${end}`,
                headers: {
                    'x-authorization': `Bearer ${cirrusSig?.tokens.rst}`,
                    'x-cirrus-cs': size.toString(),
                    'x-cirrus-device-id': deviceId,
                },
                fetchInit: {
                    method: 'GET',
                }
            }, async (response) => {
                const contentType = response.headers.get('content-type');
                if (!response.ok){
                    const jsonResult = (contentType?.includes('application/json')) ? await response.json() : null;
                    throw createError({
                        status: response.status,
                        statusText: (jsonResult && jsonResult.error ? jsonResult.error : 'Unknown error attempting to load publication')
                    });
                }
                return response.arrayBuffer();
            }).then(async (result) => {
                return Promise.resolve(result.body).catch((_err) => {
                    console.info("Format Error: Result not ArrayBuffer compatible")
                    return Promise.reject("Format Error: Result not ArrayBuffer compatible");
                });
            }),
            {
                retries: 3,
                delayMs: 500,
                jitter: 0.2,
                shouldRetry: async (_err) => {
                    const status = (typeof _err === 'object' && _err !== null)
                        ? ((_err as { statusCode?: number; status?: number }).statusCode ?? (_err as { statusCode?: number; status?: number }).status)
                        : undefined;
                    if (status == 403) return await refreshSession();
                    if (!isNuxtError(_err)) {
                        console.warn(`A non-nuxt error occurred as a result of a signed "sendRangeRequest"`, _err);
                    }
                    return true;
                },
                onRetry: async (err, attempt) => {
                    console.warn(`Retrying signed "sendRangeRequest" (attempt ${attempt + 1})`, err);
                }
            }
        );
    }
    
    const sendHeadRequest = async (): Promise<IHttpHeadResponse> => {
        const deviceId = await $cryptography.fingerprint.deviceId();
        if (!cirrusSig) await authoriseSession();

        const { mediaType, contentLength, contentType } = await useRetryFunction(
            () => signedFetch.signedFetchWithResponseBody(getCdnUrl(), {
                range: 'bytes=0-1600',
                headers: {
                    'x-authorization': `Bearer ${cirrusSig?.tokens.rst}`,
                    'x-cirrus-device-id': deviceId,
                },
                fetchInit: {
                    method: 'GET',
                }
            }, async (response) => {
                const contentType = response.headers.get('content-type');
                if(!response.ok){
                    const jsonResult = (contentType?.includes('application/json')) ? await response.json() : null;
                    throw showError({
                        fatal: true,
                        status: response.status,
                        statusText: (jsonResult && jsonResult.error ? jsonResult.error : 'Unknown error attempting to load publication')
                    });
                }
                const mediaType = await response.arrayBuffer().then((buffer) => {
                    return MediaTypeDetector.detectFromUint8Array(new Uint8Array(buffer));
                }).catch((_err) => {
                    console.info("Format Error: Result not ArrayBuffer compatible")
                    throw showError({
                        fatal: true,
                        status: 400,
                        statusText: 'The server returned an invalid publication. Try again later.'
                    });
                });

                if (!mediaType) throw showError({
                        fatal: true,
                        status: 400,
                        statusText: 'The server response was an invalid media type. Try again later.'
                    });
                
                const contentRange = response.headers.get("content-range");
                let contentLength = response.headers.get('content-length');
                if (contentRange && contentRange.indexOf("/") > 0){
                    contentLength = contentRange.split("/")[1] ?? null;
                }
                if (!contentLength || !contentType) throw showError({
                        fatal: true,
                        status: 400,
                        statusText: 'The server response was invalid. Try again later',
                        data: 'Missing headers from publication request'
                    });
                if (isNaN(parseInt(contentLength))) throw showError({
                        fatal: true,
                        status: 400,
                        statusText: 'The server response was invalid. Try again later',
                        data: 'Content-Length value is not valid'
                    });
        
                return {
                    mediaType: mediaType,
                    contentLength: parseInt(contentLength || "0"),
                    contentType: contentType
                };
            }).then((result) => result.body),
            {
                retries: 1,
                delayMs: 500,
                jitter: 0.2,
                shouldRetry: async (_err) => {
                    const status = (typeof _err === 'object' && _err !== null)
                        ? ((_err as { statusCode?: number; status?: number }).statusCode ?? (_err as { statusCode?: number; status?: number }).status)
                        : undefined;
                    if (status == 401 || status == 404) return false;
                    if (status == 403) return await refreshSession();
                    return true;
                },
                onRetry: async (err, attempt) => {
                    console.warn(`Retrying signed "sendHeadRequest" (attempt ${attempt + 1})`, err);
                },
            }
        );

        return {
            mediaType: mediaType,
            contentLength: contentLength,
            contentType: contentType,
        }
    }

    return {
        sendHeadRequest,
        sendRangeRequest
    }
};
